import { useCallback, useMemo } from 'react';
import { formatCurrency } from '@carbonify/ui/utils/format';
import useAsyncMemo from 'hooks/useAsyncMemo';
import useMarketingParams from 'hooks/useMarketingParams';
import useMarketingUrl from 'hooks/useMarketingUrl';
import useSessionStorage from 'hooks/useSessionStorage';
import { useRouter } from 'next/router';
import useConfig from 'services/config';
import { useProductOptionsData } from 'services/productOptions';
import { getPriceFromProductOption } from 'services/productOptions/helpers';
import { interpolate } from 'utils/string';

import { queryTenantApi } from './client';
import { GetReferralCampaignResponse } from './types';

function useReferralPopupData(tenant: string, campaign: string, { skip }: { skip?: boolean } = {}) {
  return useAsyncMemo<GetReferralCampaignResponse>(async () => {
    if (skip) return undefined;

    const response = await queryTenantApi({
      url: '/api/tenants/{{tenant}}/referral-campaigns/{{campaign}}',
      tags: ['ProductReferrals'],
      params: { tenant, campaign },
    });

    return response;
  }, [campaign, skip]);
}

export default function useReferralPopup() {
  const { tenant } = useConfig();
  const params = useMarketingParams();
  const getUrl = useMarketingUrl();
  const { isReady } = useRouter();
  const { utmMedium, utmCampaign } = params.utmParams || {};
  const [hiddenReferrals, setHiddenReferrals] = useSessionStorage<string[]>('cb-referral', []);
  const shouldSkipReferral =
    !isReady || !utmCampaign || utmMedium !== 'referral' || hiddenReferrals.includes(utmCampaign);
  const data = useReferralPopupData(tenant, utmCampaign!, { skip: shouldSkipReferral });
  const hasDynamicVariables = data?.activeReferralCampaign?.content.includes('{{') || false;

  const productOptionsUtms = useMemo(() => {
    const { couponCode, utmCampaign, utmSource } = data?.activeReferralCampaign || {};

    return {
      couponCodes: couponCode ? [couponCode] : undefined,
      utmParams: {
        ...params.utmParams,
        utmCampaign,
        utmSource,
      },
      skip: !hasDynamicVariables || !data?.activeReferralCampaign,
    };
  }, [data, params.utmParams, hasDynamicVariables]);

  const productOptions = useProductOptionsData(tenant, productOptionsUtms);

  const onClose = useCallback(
    () => utmCampaign && setHiddenReferrals([...hiddenReferrals, utmCampaign]),
    [hiddenReferrals, setHiddenReferrals, utmCampaign],
  );

  const ctaUrl = useMemo(
    () => getUrl(data?.activeReferralCampaign?.ctaButtonTargetUrl || '/dashboard?auth=register'),
    [data?.activeReferralCampaign?.ctaButtonTargetUrl, getUrl],
  );

  return useMemo(() => {
    if (shouldSkipReferral || !utmCampaign || !data || !data.activeReferralCampaign) return;

    if (hasDynamicVariables && (!productOptions || !('activeConfigurations' in productOptions))) return;

    const campaignFields = data.activeReferralCampaign;
    let content = campaignFields.content;

    if (hasDynamicVariables) {
      const priceMatrix = Object.entries(productOptions?.activeConfigurations || {})
        .map(([ubaYear, { productOptions, defaultVehicleClass, defaultProductOption }]) =>
          productOptions.map((item) => ({
            ...item,
            ubaYear,
            defaultVehicleClass,
            defaultProductOption,
            coupons: [
              ...item.priceBonusCoupons.map(({ couponCode }) => couponCode),
              ...item.productPriceCoupons.map(({ couponCode }) => couponCode),
            ],
          })),
        )
        .flat();

      const productOption =
        priceMatrix.find(({ coupons }) =>
          coupons.includes(campaignFields.couponCode || campaignFields.utmCampaign || ''),
        ) ||
        priceMatrix.find(({ defaultProductOption, key }) => key === defaultProductOption) ||
        priceMatrix[0];

      const coupon = campaignFields.couponCode
        ? [...productOption.priceBonusCoupons, ...productOption.productPriceCoupons].find(
            ({ couponCode }) => couponCode === campaignFields.couponCode,
          )
        : undefined;
      const price = getPriceFromProductOption(productOption, productOption.defaultVehicleClass || 'M1')!;
      const couponPrice = formatCurrency(coupon?.fixedAmount || 0);

      const values = {
        productPrice: price.relative ? `${price.value}%` : formatCurrency(price.value),
        couponPrice,
        totalPrice: price.relative
          ? `${price.value}%${coupon ? ` + ${couponPrice}` : ''}`
          : formatCurrency(price.value + (coupon?.fixedAmount || 0)),
        productOption: productOption.name,
        name: productOption.name,
        ubaYear: productOption.ubaYear,
      };

      content = interpolate(campaignFields.content, values);
    }

    return {
      ...campaignFields,
      content,
      campaign: utmCampaign,
      ctaUrl,
      onClose,
    };
  }, [data, utmCampaign, onClose, ctaUrl, productOptions, hasDynamicVariables, shouldSkipReferral]);
}
